import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import {
    ArrowDown,
    ArrowUp,
} from '@phosphor-icons/react'

import merchantApi from '../../services/merchantApi'

import './ReportAnalyticsPage.scss'
import Header from '../../components/Header/Header'
import DateRange from '../../components/DateRange/DateRange'
import { X } from 'lucide-react'
import GMVOrderTab from './components/GMVOrderTab'
import MerchantSalesTab from './components/MerchantSalesTab'

const DEFAULT_PAGINATION = {
    current_page: 1,
    per_page: 15,
    to: 0,
    from: 0,
    total: 0,
    last_page: 1,
}

const asNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback
    const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
    const number = Number(cleaned)
    return Number.isFinite(number) ? number : fallback
}

const formatMoney = (value) => {
    const amount = asNumber(value)

    return `RM${amount.toLocaleString('en-MY', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

const formatNumber = (value) => asNumber(value).toLocaleString('en-MY')

const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return null
    const number = asNumber(value)
    return `${Math.abs(number).toFixed(1)}%`
}

const formatDateLabel = (value) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)

    return date.toLocaleDateString('en-MY', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    })
}

const EMPTY_GMV_REPORT = {
    period: {},
    kpis: {
        platformGmv: 0,
        gmvVsPrevious: 0,
        totalOrders: 0,
        ordersVsPrevious: 0,
        avgOrderValue: 0,
        aovVsPrevious: 0,
        activeMerchants: 0,
        merchantsVsPrevious: 0,
    },
    highlights: {},
    trend: { current: [], previous: [] },
    rows: [],
    pagination: DEFAULT_PAGINATION,
}

const EMPTY_MERCHANT_REPORT = {
    period: {},
    highlights: {},
    rows: [],
    pagination: DEFAULT_PAGINATION,
}

const normalizeGmvReport = (response) => {
    const data = response?.data || {}
    const kpis = data.kpis || {}
    const rows = Array.isArray(data.daily_breakdown)
        ? data.daily_breakdown.map((row, index) => ({
            id: row.id ?? `${row.date || 'day'}-${index}`,
            date: formatDateLabel(row.date),
            gmv: asNumber(row.gmv),
            vs_yesterday: row.vs_yesterday,
            orders: asNumber(row.orders),
            aov: asNumber(row.aov),
            dine_in: asNumber(row.dine_in),
            takeaway: asNumber(row.takeaway),
            preorder: asNumber(row.pre_order),
        }))
        : []

    return {
        period: data.period || {},
        kpis: {
            platformGmv: asNumber(kpis.platform_gmv),
            gmvVsPrevious: asNumber(kpis.gmv_vs_previous),
            totalOrders: asNumber(kpis.total_orders),
            ordersVsPrevious: asNumber(kpis.orders_vs_previous),
            avgOrderValue: asNumber(kpis.avg_order_value),
            aovVsPrevious: asNumber(kpis.aov_vs_previous),
            activeMerchants: asNumber(kpis.active_merchants),
            merchantsVsPrevious: asNumber(kpis.merchants_vs_previous),
        },
        highlights: data.highlights || {},
        trend: {
            current: Array.isArray(data.gmv_trend?.current) ? data.gmv_trend.current : [],
            previous: Array.isArray(data.gmv_trend?.previous) ? data.gmv_trend.previous : [],
        },
        rows,
        pagination: {
            ...DEFAULT_PAGINATION,
            per_page: rows.length || DEFAULT_PAGINATION.per_page,
            total: rows.length,
            from: rows.length ? 1 : 0,
            to: rows.length,
        },
    }
}

const normalizeMerchantReport = (response) => {
    const data = response?.data || {}
    const rows = Array.isArray(data.merchant_performance)
        ? data.merchant_performance.map((row, index) => ({
            id: row.merchant_id ?? index + 1,
            merchant_name: row.merchant_name,
            pos: row.pos_system || '-',
            gmv: asNumber(row.gmv),
            vs_previous: row.vs_previous,
            prev_gmv: asNumber(row.prev_gmv),
            orders: asNumber(row.orders),
            aov: asNumber(row.aov),
            dine_in: asNumber(row.dine_in),
            takeaway: asNumber(row.takeaway),
            preorder: asNumber(row.pre_order),
        }))
        : []
    const pagination = data.pagination || {}
    const currentPage = asNumber(pagination.current_page, 1)
    const perPage = asNumber(pagination.per_page, 15)
    const total = asNumber(pagination.total)

    return {
        period: data.period || {},
        highlights: data.highlights || {},
        rows,
        pagination: {
            ...DEFAULT_PAGINATION,
            ...pagination,
            current_page: currentPage,
            per_page: perPage,
            from: total ? ((currentPage - 1) * perPage) + 1 : 0,
            to: total ? Math.min(currentPage * perPage, total) : 0,
        },
    }
}

const getRangeParams = (range) => {
    return { period: range }
}

const countDays = (from, to) => {
    if (!from || !to) return 0
    const start = new Date(from)
    const end = new Date(to)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
    return Math.floor(Math.abs(end - start) / 86400000) + 1
}

function CustomDateRangeModal({ open, fromDate, toDate, onFromChange, onToChange, onClose, onApply }) {
    if (!open) return null

    return createPortal(
        <div className="sales-range-modal-overlay" onClick={onClose}>
            <div className="sales-range-modal" onClick={(event) => event.stopPropagation()}>
                <div className="sales-range-modal__header">
                    <h3>Pick your date range</h3>
                    <button type="button" className="sales-range-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="sales-range-modal__body">
                    <DateRange
                        startDate={fromDate}
                        endDate={toDate}
                        onStartDateChange={onFromChange}
                        onEndDateChange={onToChange}
                    />
                </div>

                <div className="sales-range-modal__footer">
                    <button type="button" className="sales-range-modal__btn sales-range-modal__btn--ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className="sales-range-modal__btn sales-range-modal__btn--primary" onClick={onApply} disabled={!fromDate || !toDate}>
                        Apply
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default function ReportAnalyticsPage() {
    const [range, setRange] = useState('7days')
    const [gmvReport, setGmvReport] = useState(EMPTY_GMV_REPORT)
    const [merchantReport, setMerchantReport] = useState(EMPTY_MERCHANT_REPORT)
    const [gmvLoading, setGmvLoading] = useState(true)
    const [merchantLoading, setMerchantLoading] = useState(true)
    const [gmvError, setGmvError] = useState('')
    const [merchantError, setMerchantError] = useState('')
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [draftStartDate, setDraftStartDate] = useState(null)
    const [draftEndDate, setDraftEndDate] = useState(null)
    const [showCustomRangeModal, setShowCustomRangeModal] = useState(false)
    const [activeTab, setActiveTab] = useState('gmv_orders')
    const [merchantSalesPage, setMerchantSalesPage] = useState(1)
    const [dateRangeOptions, setDateRangeOptions] = useState([])

    const tabList = [
        { key: 'gmv_orders', label: 'GMV & Orders' },
        { key: 'merchant_sales', label: 'Merchant sales' },
    ]

    useEffect(() => {
        let active = true

        const loadGmvReport = async () => {
            setGmvLoading(true)
            setGmvError('')

            const params = {
                ...getRangeParams(range),
            }
            if (range === 'custom') {
                params.from = startDate
                params.to = endDate
            }

            try {
                const response = await merchantApi.reports.gmvOrders(params)
                if (active) {
                    setGmvReport(normalizeGmvReport(response))
                }
            } catch (err) {
                if (active) {
                    setGmvError(err?.message || 'Unable to load GMV and orders report.')
                    setGmvReport(EMPTY_GMV_REPORT)
                }
            } finally {
                if (active) {
                    setGmvLoading(false)
                }
            }
        }

        if (range !== 'custom' || (startDate && endDate)) loadGmvReport()

        return () => {
            active = false
        }
    }, [range, startDate, endDate])

    useEffect(() => {
        let active = true

        const loadMerchantReport = async () => {
            setMerchantLoading(true)
            setMerchantError('')

            const params = {
                ...getRangeParams(range),
                page: merchantSalesPage,
                per_page: merchantReport.pagination.per_page,
            }
            if (range === 'custom') {
                params.from = startDate
                params.to = endDate
            }

            try {
                const response = await merchantApi.reports.merchantSales(params)
                if (active) setMerchantReport(normalizeMerchantReport(response))
            } catch (err) {
                if (active) {
                    setMerchantError(err?.message || 'Unable to load merchant sales report.')
                    setMerchantReport(EMPTY_MERCHANT_REPORT)
                }
            } finally {
                if (active) setMerchantLoading(false)
            }
        }

        if (range !== 'custom' || (startDate && endDate)) loadMerchantReport()

        return () => {
            active = false
        }
    }, [range, startDate, endDate, merchantSalesPage])

    useEffect(() => {
        const loadGeneral = async () => {
            try {
                const response = await merchantApi.general.get()
                setDateRangeOptions(response?.data?.report_date_ranges || [])
            } catch (err) {
                // handle error
            }
        }

        loadGeneral()
    }, [])

    const gmvComparisonOptions = {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'inherit',
        },
        colors: ['#71717A', '#2ECAE0'],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '60%',
                borderRadius: 0,
            },
        },
        dataLabels: { enabled: false },
        stroke: { show: false },
        xaxis: {
            categories: (
                gmvReport.trend.current.length
                    ? gmvReport.trend.current
                    : gmvReport.trend.previous
            ).map((item) => item.day || formatDateLabel(item.date)),
            axisBorder: { show: true, color: '#D4D4D8' },
            axisTicks: { show: false },
            labels: {
                style: { colors: '#52525B', fontSize: '12px' },
            },
        },
        yaxis: {
            min: 0,
            tickAmount: 5,
            labels: {
                formatter: (value) => Math.round(value).toLocaleString(),
                style: { colors: '#52525B', fontSize: '12px' },
            },
        },
        grid: {
            show: true,
            borderColor: '#D4D4D8',
            strokeDashArray: 3,
            padding: { top: 0, right: 12, bottom: 0, left: 8 },
        },
        fill: { opacity: 0.85 },
        legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
            inverseOrder: true,
            fontSize: '12px',
            labels: { colors: '#52525B' },
            markers: { width: 10, height: 10, radius: 0 },
            itemMargin: { horizontal: 12, vertical: 4 },
        },
        tooltip: {
            y: {
                formatter: (value) => `RM ${value.toLocaleString('en-MY')}`,
            },
        },
    }

    const gmvComparisonSeries = [
        {
            name: 'Previous',
            data: gmvReport.trend.previous.map((item) => asNumber(item.gmv)),
        },
        {
            name: 'This period',
            data: gmvReport.trend.current.map((item) => asNumber(item.gmv)),
        },
    ]

    const renderTrend = (value, fallbackText) => {
        if (value === null || value === undefined || value === '') {
            return <div className="trend">{fallbackText}</div>
        }

        const trend = formatPercent(value)
        const positive = asNumber(value) >= 0

        return (
            <div className={`trend ${positive ? 'positive' : 'negative'}`}>
                {positive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {trend || fallbackText}
            </div>
        )
    }

    const handleRangeSelect = (nextRange) => {
        if (nextRange === 'custom') {
            setDraftStartDate(startDate)
            setDraftEndDate(endDate)
            setShowCustomRangeModal(true)
            return
        }

        setShowCustomRangeModal(false)
        setMerchantSalesPage(1)
        setRange(nextRange)
    }

    const handleApplyCustomRange = () => {
        if (!draftStartDate || !draftEndDate) return

        setStartDate(draftStartDate)
        setEndDate(draftEndDate)
        setMerchantSalesPage(1)
        setRange('custom')
        setShowCustomRangeModal(false)
    }

    const visibleLoading = activeTab === 'gmv_orders' ? gmvLoading : merchantLoading
    const visibleError = activeTab === 'gmv_orders' ? gmvError : merchantError
    const period = gmvReport.period?.from ? gmvReport.period : merchantReport.period
    const periodDays = countDays(period?.from, period?.to)

    return (
        <div className="report-analytics-page">
            <Header
                items={[
                    { label: 'Cuscapi', to: '/' },
                    { label: 'Reports & analytics', current: true },
                ]}
            />
            <div className="page-header">
                <div>
                    <h1>
                        Reports & analytics
                    </h1>
                    <p>
                        Platform-wide data across all merchants
                    </p>
                </div>

                <div className="report-actions">
                    <div className="report-tabs">
                        {
                            dateRangeOptions.map(option => (
                                <button
                                    key={option.value}
                                    className={range === option.value ? 'active' : ''}
                                    onClick={() => handleRangeSelect(option.value)}
                                    type="button"
                                >
                                    {option.label}
                                </button>
                            ))
                        }
                    </div>
                </div>
            </div>

            {visibleError ? (
                <div className="report-alert">
                    {visibleError}
                </div>
            ) : null}

            {visibleLoading ? (
                <div className="report-loading">
                    Loading sales report...
                </div>
            ) : null}
            <div className="period-range">
                Showing <span>{period?.from || '-'} to {period?.to || '-'} ({periodDays} days)</span>
            </div>
            <div className="ra-stats-grid">
                <div className="ra-stat-card">
                    <span>
                        PLATFORM GMV
                    </span>

                    <h2>
                        {formatMoney(gmvReport.kpis.platformGmv)}
                    </h2>

                    {renderTrend(gmvReport.kpis.gmvVsPrevious, '0.0%')}
                </div>

                <div className="ra-stat-card">
                    <span>
                        TOTAL ORDERS
                    </span>

                    <h2>{formatNumber(gmvReport.kpis.totalOrders)}</h2>

                    {renderTrend(gmvReport.kpis.ordersVsPrevious, '0.0%')}
                </div>

                <div className="ra-stat-card">
                    <span>
                        AVG ORDER VALUE
                    </span>

                    <h2>
                        {formatMoney(gmvReport.kpis.avgOrderValue)}
                    </h2>

                    {renderTrend(gmvReport.kpis.aovVsPrevious, '0.0%')}
                </div>
                <div className="ra-stat-card">
                    <span>
                        ACTIVE MERCHANTS
                    </span>

                    <h2>
                        {formatNumber(gmvReport.kpis.activeMerchants)}
                    </h2>
                    {renderTrend(gmvReport.kpis.merchantsVsPrevious, '0.0%')}
                </div>
            </div>

            <div className="ra-tabs">
                {
                    tabList.map((tab) => (
                        <button
                            key={tab.key}
                            className={`ra-tab ${activeTab === tab.key ? 'ra-tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))
                }
            </div>

            {activeTab === 'gmv_orders' && (
                <GMVOrderTab
                    chartOptions={gmvComparisonOptions}
                    chartSeries={gmvComparisonSeries}
                    highlights={gmvReport.highlights}
                    rows={gmvReport.rows}
                    pagination={gmvReport.pagination}
                    loading={gmvLoading}
                    formatMoney={formatMoney}
                    renderTrend={renderTrend}
                />
            )}
            {activeTab === 'merchant_sales' && (
                <MerchantSalesTab
                    highlights={merchantReport.highlights}
                    rows={merchantReport.rows}
                    pagination={{ ...merchantReport.pagination, current_page: merchantSalesPage }}
                    loading={merchantLoading}
                    onPageChange={setMerchantSalesPage}
                    formatMoney={formatMoney}
                    renderTrend={renderTrend}
                />
            )}

            <CustomDateRangeModal
                open={showCustomRangeModal}
                fromDate={draftStartDate}
                toDate={draftEndDate}
                onFromChange={setDraftStartDate}
                onToChange={setDraftEndDate}
                onClose={() => setShowCustomRangeModal(false)}
                onApply={handleApplyCustomRange}
            />
        </div>
    )
}
